import type { CityConfig } from "@/pages/city-template/types";

export const sedonaConfig: CityConfig = {
  city: "Sedona",
  state: "Arizona",
  stateAbbr: "AZ",
  slug: "sedona",
  route: "/sedona",
  title: "Emergency & Expedited Passport Services Sedona AZ | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Sedona, AZ — as fast as 24 hours. No Sedona passport agency. Fast Passport Center couriers to the Western Passport Center in Tucson, about 230 miles south. A+ BBB rated. Serving Red Rock Country, Oak Creek Canyon, and Yavapai County travelers.",
  h1: "Emergency & Expedited Passport Services in Sedona, AZ",
  heroSubtitle: "Sedona is one of the world's most iconic destinations, nestled in Arizona's Red Rock Country about 230 miles from the Western Passport Center in Tucson. Fast Passport Center's registered couriers deliver your documents to Tucson so you get a new or renewed passport as fast as 24 hours — whether you're a Sedona artist with an international exhibition, a spiritual retreat leader traveling abroad, or a Verde Valley family with international vacation plans.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Sedona%20Arizona%20red%20rock%20formations%20at%20golden%20hour%20with%20Cathedral%20Rock%20dramatically%20illuminated%20by%20warm%20sunset%20light%2C%20clear%20desert%20sky%20with%20subtle%20clouds%2C%20high%20desert%20vegetation%20in%20foreground%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20composition%20emphasizing%20the%20iconic%20red%20sandstone%20formations%2C%20no%20text%20or%20people%20visible%2C%20pristine%20natural%20beauty&width=1800&height=700&seq=sedona-hero-001&orientation=landscape",
  heroImageAlt: "Sedona Arizona red rock formations and Cathedral Rock at golden sunset hour",
  geoPosition: "34.8697;-111.7610",
  geoPlacename: "Sedona, Arizona",
  lastModified: "2026-06-05",
  regionalAgency: {
    name: "Western Passport Center",
    address: "733 N Stone Ave, Tucson, AZ 85701",
    distance: "Approximately 230 miles from Sedona — about 4 hours via I-17 and I-10",
  },
  nearbyCitiesZips: [
    { name: "West Sedona", zips: "86336" },
    { name: "Uptown Sedona", zips: "86336" },
    { name: "Oak Creek Canyon", zips: "86336" },
    { name: "Village of Oak Creek", zips: "86351" },
    { name: "Cottonwood", zips: "86326" },
    { name: "Cornville", zips: "86325" },
    { name: "Clarkdale", zips: "86324" },
    { name: "Jerome", zips: "86331" },
    { name: "Camp Verde", zips: "86322" },
    { name: "Rimrock", zips: "86335" },
    { name: "Lake Montezuma", zips: "86342" },
    { name: "Flagstaff", zips: "86001, 86004" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Western Passport Center in Tucson — 230 miles from Sedona" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Red Rock Hub", desc: "Serving Sedona, Village of Oak Creek, Cottonwood, and Verde Valley" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Sedona case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Flagstaff AZ", route: "/flagstaff" },
    { name: "Expedited passport in Cottonwood AZ", route: "/cottonwood" },
    { name: "Urgent passport in Phoenix AZ", route: "/phoenix" },
    { name: "Arizona passport hub", route: "/arizona" },
    { name: "Expedited passport in Prescott AZ", route: "/prescott" },
    { name: "Fast passport in Scottsdale AZ", route: "/scottsdale" },
    { name: "Emergency passport in Prescott Valley AZ", route: "/prescott-valley" },
  ],
  fpcOfficeAddress: "4742 N 24th St, Phoenix, AZ 85016",
  fpcOfficeNote: "Fast Passport Center serves Sedona remotely via secure courier. Our registered couriers hand-deliver your documents to the Western Passport Center in Tucson. Sedona sits in the Verde Valley along the AZ-179 and I-17 corridor, making overnight document shipping fast and reliable from Red Rock Country.",
  fpcOfficeDistance: "Serving all of Verde Valley and Red Rock Country",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Sedona, Village of Oak Creek, Cottonwood, and the Verde Valley. From international artists to spiritual retreat leaders, we help Sedona travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Sedona and nearby communities including Village of Oak Creek, Cottonwood, Clarkdale, Jerome, Camp Verde, Cornville, and the entire Verde Valley.",
  howItWorksIntro: "Most Sedona travelers complete everything online and ship documents overnight. At about 230 miles from Tucson, Fast Passport Center provides fast courier turnaround for Verde Valley residents — ideal for Sedona's international art community and spiritual tourism professionals.",
  mistakesIntro: "Sedona travelers, including international artists exhibiting overseas and spiritual retreat leaders traveling globally, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Sedona and Verde Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Sedona? Here are the most common ones we hear from Red Rock Country travelers.",
  statePageRoute: "/states/arizona",
  statePageName: "Arizona",
  dmvWebsite: "https://azdot.gov/motor-vehicles",
  dmvLabel: "Arizona MVD — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "About 230 miles to Tucson (4-hour drive)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for Sedona artists with international exhibitions",
        "Perfect for spiritual retreat leaders traveling globally",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from FLG or PHX",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Sedona is about 230 miles from the Western Passport Center in Tucson. Our couriers make rush delivery possible, as fast as 24 hours.",
      bullets: ["Red Rock Country coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in AZ",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Sedona adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Sedona and Verde Valley"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Sedona and Verde Valley.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Sedona, Village of Oak Creek, and Verde Valley residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Tucson agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in the Sedona area.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Sedona and Verde Valley residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Sedona AZ", description: "Urgent passport processing for Sedona, AZ travelers via Western Passport Center courier — about 230 miles from Sedona." },
    { name: "Passport Renewal Sedona AZ", description: "Fast passport renewal for Sedona and Verde Valley, Arizona residents." },
    { name: "New Passport Sedona AZ", description: "First-time passport application assistance for Sedona and Red Rock Country residents." },
    { name: "Lost Passport Sedona AZ", description: "Passport replacement for lost or stolen passports for Sedona and Verde Valley residents." },
    { name: "Child Passport Sedona AZ", description: "Minor passport services for Sedona and Verde Valley families." },
    { name: "Name Change Passport Sedona AZ", description: "Passport name change service for Sedona, AZ residents." },
  ],
  testimonials: [
    {
      name: "Elena R.",
      location: "Uptown Sedona Artist",
      rating: 5,
      text: "I had a gallery opening in Paris and realized my passport was expired 4 days before my flight. Fast Passport Center handled everything remotely — I had my new passport in 36 hours. Made it to my own opening.",
      service: "Emergency Renewal",
    },
    {
      name: "David K.",
      location: "Village of Oak Creek",
      rating: 5,
      text: "Yoga retreat leader with workshops in Bali and Costa Rica. My passport renewal was delayed through the Cottonwood post office. Fast Passport Center couriered everything to Tucson and I had my passport in 5 days.",
      service: "Expedited Renewal",
    },
    {
      name: "Margaret S.",
      location: "West Sedona",
      rating: 5,
      text: "Family trip to Italy over spring break. Needed passports for two teens — first time applicants. Fast Passport Center guided us through the DS-11 at the Sedona post office, then rushed processing. Both passports arrived in 6 days.",
      service: "Family Passports",
    },
    {
      name: "Robert C.",
      location: "Cornville, AZ",
      rating: 5,
      text: "Retired chef doing a culinary tour of Southeast Asia. Lost my passport 3 days before departure. Fast Passport Center got me an emergency replacement through Tucson in 48 hours. Didn't miss a single booking.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "sed-1",
      name: "Sedona Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "190 W State Rte 89A",
      city: "Sedona",
      state: "AZ",
      zip: "86336",
      phone: "(928) 282-3511",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "Uptown Sedona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Sedona. Appointment required. Call ahead for passport service hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sed-2",
      name: "Yavapai County Clerk — Cottonwood Office",
      type: "County Clerk Passport Acceptance",
      address: "10 S 6th St",
      city: "Cottonwood",
      state: "AZ",
      zip: "86326",
      phone: "(928) 639-8130",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "15 miles southwest in Cottonwood",
      website: "https://courts.yavapaiaz.gov/clerk",
      websiteLabel: "Yavapai County Clerk",
      notes: "Accepts passport applications for Verde Valley residents. Convenient for Cottonwood and Cornville residents.",
      icon: "ri-government-line",
    },
    {
      id: "sed-3",
      name: "Cottonwood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 S Willard St",
      city: "Cottonwood",
      state: "AZ",
      zip: "86326",
      phone: "(928) 634-2281",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from Sedona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient alternative for Cottonwood and Verde Valley residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sed-4",
      name: "Clarkdale Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "901 Main St",
      city: "Clarkdale",
      state: "AZ",
      zip: "86324",
      phone: "(928) 634-7100",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "20 miles southwest of Sedona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Clarkdale and Jerome area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sed-5",
      name: "Camp Verde Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "464 S 1st St",
      city: "Camp Verde",
      state: "AZ",
      zip: "86322",
      phone: "(928) 567-3081",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "28 miles south of Sedona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Camp Verde and Lake Montezuma residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sed-6",
      name: "Flagstaff Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2400 N Postal Blvd",
      city: "Flagstaff",
      state: "AZ",
      zip: "86004",
      phone: "(928) 714-9301",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "30 miles north of Sedona",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern Verde Valley and Oak Creek Canyon residents. Larger facility with more appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Tucson Agency", agency: "230 miles — 4-hr drive, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming a 4-Hour Drive to Tucson Solves Everything",
      description: "The Western Passport Center is 230 miles from Sedona — about a 4-hour drive each way. And you still need an appointment. Walk-ins are turned away. An 8-hour round trip with no guarantee of service is not a plan. We handle the courier delivery so you stay in Red Rock Country.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Specifications",
      description: "Passport photos must be exactly 2x2 inches with a white background. While Sedona's CVS and Walgreens are generally reliable, always verify your photos meet the exact requirements — especially important for artists and professionals with international engagements.",
    },
    {
      icon: "ri-file-list-line",
      title: "Using the Wrong Renewal Form",
      description: "Many Sedona residents who travel frequently assume they can always use DS-82 for renewal. If your passport was issued more than 15 years ago or when you were under 16, you need DS-11 and an in-person appearance at an acceptance facility.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Not Having Proof of Travel for Emergency Processing",
      description: "Emergency processing at the Western Passport Center requires confirmed travel documents. Have your flight itinerary or event confirmation ready before reaching out — especially important for artists with exhibition dates and retreat leaders with fixed schedules.",
    },
    {
      icon: "ri-parent-line",
      title: "Both Parents Required for Minor Passports",
      description: "Minors under 16 require both parents to appear at the acceptance facility, or one with notarized Form DS-3053. Sedona families with complex schedules or one parent traveling often miss this requirement.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Standard USPS processing still takes 8-11 weeks. Sedona's tourism professionals and artists with international commitments should start passport applications at least 8 weeks before any scheduled travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Sedona, Village of Oak Creek, or Cottonwood." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Western Passport Center in Tucson — about 230 miles away." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Western Passport Center via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Sedona or Verde Valley address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Sedona from a passport agency?",
      a: "Sedona is approximately 230 miles from the Western Passport Center in Tucson — about a 4-hour drive via I-17 and I-10. The Tucson agency requires pre-scheduled appointments and does not accept walk-ins. Fast Passport Center handles the courier delivery so you don't need to make the trip.",
    },
    {
      q: "Can Sedona artists and tourism professionals get fast passports?",
      a: "Yes. Sedona's international art community and spiritual tourism professionals are some of our most frequent customers. We can process passports in 2–9 business days with standard expedited service, or 24–48 hours for emergency processing. Start early — ideally 8 weeks before any international event.",
    },
    {
      q: "How fast can I get an emergency passport in Sedona?",
      a: "Emergency processing is typically 24–48 hours for Sedona residents. Our couriers deliver directly to the Western Passport Center in Tucson, about 230 miles away. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Does Sedona have its own passport agency?",
      a: "No. The closest regional passport agency is the Western Passport Center in Tucson, about 230 miles from Sedona. Sedona does have an acceptance facility at the Sedona Post Office on State Route 89A for first-time applications requiring in-person submission.",
    },
    {
      q: "What if I need a passport for an international art exhibition or spiritual retreat?",
      a: "Sedona's creative and spiritual community frequently travels internationally for gallery openings, art fairs, and retreat leadership. Fast Passport Center understands the urgency of these commitments and provides expedited passport services to match your timeline.",
    },
    {
      q: "Where do Sedona travelers fly internationally?",
      a: "Sedona travelers typically fly internationally through Flagstaff Pulliam Airport (FLG, 30 miles north with connections to PHX/DEN), Phoenix Sky Harbor International Airport (PHX, 120 miles south), or sometimes Las Vegas (LAS, 270 miles northwest). A valid U.S. passport is required for all international flights.",
    },
    {
      q: "How do I renew my passport in Sedona?",
      a: "Adult renewals (passport issued at 16 or older, within the past 15 years, in your current name) can be done entirely by mail with Form DS-82. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks).",
    },
    {
      q: "Where can I get passport photos in Sedona?",
      a: "The Sedona Post Office on 89A accepts passport applications but may not offer photo services. CVS Pharmacy, Walgreens, and FedEx Office in Cottonwood offer passport photo services. Always verify your photos meet the exact 2x2 inch specification.",
    },
    {
      q: "What documents do I need for a first-time passport in Sedona?",
      a: "You'll need Form DS-11, a certified birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility such as the Sedona Post Office or Cottonwood Post Office.",
    },
    {
      q: "Can you serve the entire Verde Valley?",
      a: "Yes. We serve all of the Verde Valley including Sedona, Village of Oak Creek, Cottonwood, Clarkdale, Jerome, Cornville, Camp Verde, and Rimrock. Documents ship overnight from any UPS location in the Verde Valley, and we deliver your passport back to your address.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Cottonwood", address: "1129 S Main St, Cottonwood, AZ 86326", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walgreens — Cottonwood", address: "300 S Main St, Cottonwood, AZ 86326", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Flagstaff", address: "2620 S Woodlands Village Blvd, Flagstaff, AZ 86001", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–5PM" },
    { name: "Walgreens — Flagstaff", address: "1500 E Cedar Ave, Flagstaff, AZ 86004", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Flagstaff", address: "2610 E Rte 66, Flagstaff, AZ 86004", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Cottonwood", address: "1672 E Cottonwood St, Ste B, Cottonwood, AZ 86326", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–4PM" },
  ],
  dmvLocations: [
    { name: "AZ MVD — Cottonwood", address: "525 S 12th St, Cottonwood, AZ 86326", hours: "Mon–Fri: 8:00AM–5:00PM", note: "REAL ID and driver's license renewals for Verde Valley residents. Closest full-service MVD to Sedona.", featured: true },
    { name: "AZ MVD — Flagstaff", address: "1959 S Woodlands Village Blvd, Flagstaff, AZ 86001", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves northern Arizona including Sedona and Oak Creek Canyon residents.", featured: false },
    { name: "AZ MVD — Prescott", address: "1105 Commerce Dr, Prescott, AZ 86305", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves western Yavapai County including Jerome and Clarkdale.", featured: false },
  ],
  airports: [
    {
      code: "FLG",
      name: "Flagstaff Pulliam Airport",
      distance: "30 miles north of Sedona",
      address: "6200 S Pulliam Dr, Flagstaff, AZ 86005",
      note: "Closest commercial airport to Sedona with connections to Phoenix and Denver via American Airlines. About 45 minutes north via AZ-89A and I-17. Passport required for international connections.",
      flightUrl: "https://www.flagstaff.az.gov/123/Airport",
      flightLabel: "FLG Airport",
      featured: true,
    },
    {
      code: "PHX",
      name: "Phoenix Sky Harbor International Airport",
      distance: "120 miles south of Sedona",
      address: "3400 E Sky Harbor Blvd, Phoenix, AZ 85034",
      note: "Arizona's largest international airport with extensive domestic and global routes. About 2 hours south via I-17.",
      flightUrl: "https://www.skyharbor.com/",
      flightLabel: "PHX Airport",
      featured: false,
    },
    {
      code: "PRC",
      name: "Prescott Regional Airport",
      distance: "65 miles southwest of Sedona",
      address: "6500 MacCurdy Dr, Prescott, AZ 86301",
      note: "Regional airport with connections to Denver and Los Angeles. About 1.5 hours southwest via AZ-89A and AZ-89.",
      flightUrl: "https://www.prcairport.com/",
      flightLabel: "PRC Airport",
      featured: false,
    },
  ],
};